import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { useDirtyFieldValidation, isValid } from '@totalsoft/pure-validations-react';
import { financialProductRuleValidator } from '../../validators';
import { financialProductRuleListValidator } from '../../validators';
import { useToast } from 'hooks/toasts/useToast';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { FINANCIAL_PRODUCT_RULE_GENERAL_INFO_QUERY } from '../../queries';
import { ADD_UPDATE_FINANCIAL_PRODUCT_RULE } from '../../mutations';
import { FINANCIAL_PRODUCT_RULE_UPDATED } from '../../subscriptions';
import ContentPaste from '@mui/icons-material/ContentPaste';
import FinancialProductRulesEditGeneralDataComponent from './FinancialProductRulesEditGeneralDataComponent';
import { Card, IconButton } from '@totalsoft/rocket-ui';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty } from '@totalsoft/change-tracking';
import { getErrors } from '@totalsoft/pure-validations-react';
import useHistoryWithContext from 'components/menu/useHistoryWithContext';
import { Grid } from '@mui/material';
import { Typography } from '@totalsoft/rocket-ui';

const defaultFinancialProductRule = {
  financialProductRuleId: null,
  productId: null,
  orderNo: null,
  ruleCode: null,
  ruleDescription: null,
  generalYN: false,
  filterCondition: null
};

const FinancialProductRulesEditGeneralDataContainer = ({ canWrite, productId, ruleId, onRedirect, onChangeTitle }) => {
  const addToast = useToast();
  const [t] = useTranslation();
  const historyContext = useHistoryWithContext();

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens({ ...defaultFinancialProductRule, productId: productId });
  const [saving, setSaving] = useState(false);
  const [financialProductRuleValidation, validateFinancialProductRule] = useDirtyFieldValidation(financialProductRuleValidator);
  const [financialProductRuleListValidation, validateFinancialProductRuleList] = useDirtyFieldValidation(financialProductRuleListValidator);

  useQuery(FINANCIAL_PRODUCT_RULE_GENERAL_INFO_QUERY, {
    variables: { ruleId },
    onCompleted: data => {
      resetLens(data.financialProductRule);
      onChangeTitle(data.financialProductRule.ruleCode);
    },
    skip: !ruleId
  });

  const [addOrUpdateFinancialProductRule] = useMutation(ADD_UPDATE_FINANCIAL_PRODUCT_RULE, {
    onError: () => {
      setSaving(false);
    }
  });

  useSubscription(FINANCIAL_PRODUCT_RULE_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.Details.Rules.GeneralData.FinancialProductRuleUpdated'), 'success');
      setSaving(false);
      if (ruleId === undefined) {
        client.cache.modify({
          id: `FinancialProductItem:{"productId":${data.financialProductRuleUpdated.financialProductRule.productId}}`,
          fields: {
            financialProductRules: (previous, { toReference }) => {
              return [...previous, toReference(data.financialProductRuleUpdated.financialProductRule)];
            }
          }
        });
        onRedirect(data.financialProductRuleUpdated.financialProductRule.financialProductRuleId);
      }
    }
  });

  const handleSave = useCallback(() => {
    const input = lens |> get;
    if (!validateFinancialProductRule(input)) return;

    let financialProductRulesData = {
      financialProductRules: historyContext?.location?.state?.financialProductRules,
      currentRuleId: ruleId,
      currentGeneralRuleYN: input.generalYN
    };
    if (!validateFinancialProductRuleList(financialProductRulesData)) return;

    setSaving(true);
    addOrUpdateFinancialProductRule({ variables: { input } });
  }, [
    addOrUpdateFinancialProductRule,
    lens,
    validateFinancialProductRule,
    validateFinancialProductRuleList,
    historyContext?.location?.state?.financialProductRules,
    ruleId
  ]);

  useEffect(() => {
    validateFinancialProductRule(lens |> get, dirtyInfo);
  }, [dirtyInfo, lens, validateFinancialProductRule]);

  useEffect(() => {
    const input = lens |> get;
    let financialProductRulesData = {
      financialProductRules: historyContext?.location?.state?.financialProductRules,
      currentRuleId: ruleId,
      currentGeneralRuleYN: input.generalYN
    };
    validateFinancialProductRuleList(financialProductRulesData);
  }, [lens, validateFinancialProductRuleList, historyContext?.location?.state?.financialProductRules, ruleId]);

  return (
    <Card
      icon={ContentPaste}
      iconColor='primary'
      title={t('FinancialProduct.Details.Rules.GeneralData.Title')}
      actions={
        <IconButton
          type='save'
          fontSize='medium'
          color='transparent'
          tooltip={t('SaveChanges')}
          onClick={handleSave}
          disabled={
            !isDirty(dirtyInfo) ||
            saving ||
            !canWrite ||
            !isValid(financialProductRuleValidation) ||
            !isValid(financialProductRuleListValidation)
          }
        />
      }
    >
      <Prompt
        when={ruleId !== undefined && isDirty(dirtyInfo)}
        message={t('FinancialProduct.Details.Rules.GeneralData.LeavingWithoutSaving')}
      />
      <FinancialProductRulesEditGeneralDataComponent canWrite={canWrite} rule={lens} validation={financialProductRuleValidation} />
      <Grid item xs={12}>
        <Typography color='error'>{financialProductRuleListValidation.financialProductRules |> getErrors}</Typography>
      </Grid>
    </Card>
  );
};

FinancialProductRulesEditGeneralDataContainer.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  productId: PropTypes.number.isRequired,
  ruleId: PropTypes.number,
  onRedirect: PropTypes.func,
  onChangeTitle: PropTypes.func
};

export default FinancialProductRulesEditGeneralDataContainer;
